import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { DateRange } from 'react-day-picker';
import { format, subDays } from 'date-fns';
import { Download, FileText, FileSpreadsheet, TrendingUp, DollarSign, BarChart3 } from 'lucide-react';
import { useReportGenerator } from '@/hooks/useReportGenerator';

interface ReportGeneratorProps {
  psychologistId?: string;
}

type ReportType = 'financial' | 'fiscal' | 'trends';
type ReportFormat = 'pdf' | 'excel';

interface ReportData {
  success: boolean;
  data: {
    summary?: {
      total_transactions?: number;
      total_amount?: number;
      avg_ticket?: number;
      success_rate?: number;
      gross_revenue?: number;
      net_revenue?: number;
      platform_fees?: number;
      tax_estimate?: number;
    };
    transactions?: Array<{
      id: string;
      user_name: string;
      psychologist_name: string;
      amount: number;
      status: string;
      payment_method: string;
      payment_provider: string;
      created_at: string;
    }>;
    growth_analysis?: {
      current_period_amount?: number;
      avg_monthly_growth?: number;
    };
    predictions?: {
      next_month_estimate?: number;
      next_quarter_estimate?: number;
    };
    monthly_data?: Array<{
      month: string;
      transactions: number;
      amount: number;
      avg_ticket: number;
    }>;
  };
}

const ReportGenerator: React.FC<ReportGeneratorProps> = ({ psychologistId }) => {
  const [reportType, setReportType] = useState<ReportType>('financial');
  const [reportFormat, setReportFormat] = useState<ReportFormat>('pdf');
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date()
  });

  const { generateReport, isGenerating } = useReportGenerator();

  // Preview dos dados do relatório
  const { data: previewData, isLoading: isLoadingPreview } = useQuery({
    queryKey: ['report-preview', reportType, dateRange, psychologistId],
    queryFn: async () => {
      if (!dateRange?.from || !dateRange?.to) return null;

      const { data, error } = await supabase.rpc('get_financial_report_data', {
        p_start_date: format(dateRange.from, 'yyyy-MM-dd'),
        p_end_date: format(dateRange.to, 'yyyy-MM-dd'),
        p_psychologist_id: psychologistId || null,
        p_report_type: reportType
      });

      if (error) throw error;
      return data as unknown as ReportData;
    },
    enabled: !!dateRange?.from && !!dateRange?.to
  });

  const handleGenerateReport = async () => {
    if (!dateRange?.from || !dateRange?.to) {
      return;
    }

    await generateReport({
      reportType,
      format: reportFormat,
      startDate: format(dateRange.from, 'yyyy-MM-dd'),
      endDate: format(dateRange.to, 'yyyy-MM-dd'),
      psychologistId
    });
  };

  const getReportTypeIcon = (type: ReportType) => {
    switch (type) {
      case 'financial': return <DollarSign className="h-4 w-4" />;
      case 'fiscal': return <FileText className="h-4 w-4" />;
      case 'trends': return <TrendingUp className="h-4 w-4" />;
    }
  };

  const getReportTypeLabel = (type: ReportType) => {
    switch (type) {
      case 'financial': return 'Relatório Financeiro';
      case 'fiscal': return 'Relatório Fiscal/Contábil';
      case 'trends': return 'Análise de Tendências';
    }
  };

  const renderPreview = () => {
    if (isLoadingPreview) {
      return <div className="text-center py-8">Carregando prévia...</div>;
    }

    if (!previewData?.success) {
      return <div className="text-center py-8 text-muted-foreground">Nenhum dado encontrado</div>;
    }

    const data = previewData.data;

    if (reportType === 'financial') {
      return (
        <div className="space-y-4">
          {/* Cards de resumo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Total Transações</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data.summary?.total_transactions || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Valor Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">R$ {(data.summary?.total_amount || 0).toFixed(2)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Ticket Médio</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">R$ {(data.summary?.avg_ticket || 0).toFixed(2)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Taxa de Sucesso</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(data.summary?.success_rate || 0).toFixed(1)}%</div>
              </CardContent>
            </Card>
          </div>

          {/* Tabela de transações */}
          {data.transactions && data.transactions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Últimas Transações</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuário</TableHead>
                      <TableHead>Psicólogo</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Gateway</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.transactions.slice(0, 10).map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{transaction.user_name}</TableCell>
                        <TableCell>{transaction.psychologist_name}</TableCell>
                        <TableCell>R$ {transaction.amount.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant={transaction.status === 'completed' ? 'default' : 'secondary'}>
                            {transaction.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{transaction.payment_provider}</TableCell>
                        <TableCell>
                          {format(new Date(transaction.created_at), 'dd/MM/yyyy')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      );
    }

    if (reportType === 'fiscal') {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Receita Bruta</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R$ {(data.summary?.gross_revenue || 0).toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Receita Líquida</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R$ {(data.summary?.net_revenue || 0).toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Taxas da Plataforma</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R$ {(data.summary?.platform_fees || 0).toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Estimativa de Impostos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R$ {(data.summary?.tax_estimate || 0).toFixed(2)}</div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (reportType === 'trends') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Período Atual</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  R$ {(data.growth_analysis?.current_period_amount || 0).toFixed(2)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Estimativa Próximo Mês</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  R$ {(data.predictions?.next_month_estimate || 0).toFixed(2)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Estimativa Próximo Trimestre</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  R$ {(data.predictions?.next_quarter_estimate || 0).toFixed(2)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Gerador de Relatórios Financeiros</h2>
        <p className="text-muted-foreground">
          Gere relatórios financeiros detalhados em PDF ou Excel
        </p>
      </div>

      {/* Configuração do Relatório */}
      <Card>
        <CardHeader>
          <CardTitle>Configurar Relatório</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Tipo de Relatório</label>
              <Select value={reportType} onValueChange={(value: ReportType) => setReportType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="financial">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Relatório Financeiro
                    </div>
                  </SelectItem>
                  <SelectItem value="fiscal">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Relatório Fiscal/Contábil
                    </div>
                  </SelectItem>
                  <SelectItem value="trends">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Análise de Tendências
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Formato</label>
              <Select value={reportFormat} onValueChange={(value: ReportFormat) => setReportFormat(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      PDF
                    </div>
                  </SelectItem>
                  <SelectItem value="excel">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4" />
                      Excel
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Período</label>
              <DatePickerWithRange
                date={dateRange}
                setDate={setDateRange}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getReportTypeIcon(reportType)}
              <span className="font-medium">{getReportTypeLabel(reportType)}</span>
              <Badge variant="outline">{reportFormat.toUpperCase()}</Badge>
            </div>
            
            <Button
              onClick={handleGenerateReport}
              disabled={isGenerating || !dateRange?.from || !dateRange?.to}
            >
              {isGenerating ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                  Gerando...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Gerar Relatório
                </div>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Prévia do Relatório */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Prévia do Relatório
          </CardTitle>
        </CardHeader>
        <CardContent>
          {renderPreview()}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportGenerator;
